import React, { useState, useEffect } from 'react';
import Button from '../Button/Button';
import './EditUserModal.css';

interface EditUserModalProps {
  isOpen: boolean;
  onClose: () => void;
  onSave: (userId: string, userData: { nom: string; prenom: string; email: string; fonction: string; role?: string }) => void;
  userId: string;
  userName: string;
  userEmail: string;
  userPrenom?: string;
  userFonction?: string;
  userRole?: string; // Ajout du rôle pour déterminer la route API
}

const EditUserModal: React.FC<EditUserModalProps> = ({
  isOpen,
  onClose,
  onSave,
  userId,
  userName,
  userEmail,
  userPrenom = '',
  userFonction = '',
  userRole = ''
}) => {
  const [nom, setNom] = useState('');
  const [prenom, setPrenom] = useState('');
  const [email, setEmail] = useState('');
  const [fonction, setFonction] = useState('');
  const [role, setRole] = useState('');
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [errors, setErrors] = useState<{ nom?: string; prenom?: string; email?: string; fonction?: string; role?: string }>({});

  // Reset form when user changes
  useEffect(() => {
    if (isOpen) {
      // userName contient le nom complet "Prenom Nom", il faut extraire le nom de famille
      const fullName = userName || '';
      const prenomValue = userPrenom || '';

      // Extraire le nom de famille en retirant le prénom du début
      let nomValue = fullName;
      if (prenomValue && fullName.startsWith(prenomValue)) {
        nomValue = fullName.substring(prenomValue.length).trim();
      }

      setNom(nomValue);
      setPrenom(prenomValue);
      setEmail(userEmail || '');
      setFonction(userFonction || '');
      setRole(userRole || '');
      setErrors({});
    }
  }, [isOpen, userName, userPrenom, userEmail, userFonction, userRole]);

  // Handle Escape key press
  useEffect(() => {
    const handleEsc = (event: KeyboardEvent) => {
      if (event.key === 'Escape' && isOpen) {
        onClose();
      }
    };
    window.addEventListener('keydown', handleEsc);

    return () => {
      window.removeEventListener('keydown', handleEsc);
    };
  }, [isOpen, onClose]);

  // Prevent body scroll when modal is open
  useEffect(() => {
    if (isOpen) {
      document.body.style.overflow = 'hidden';
    } else {
      document.body.style.overflow = '';
    }
    return () => {
      document.body.style.overflow = '';
    };
  }, [isOpen]);

  const validateForm = (): boolean => {
    const newErrors: { nom?: string; prenom?: string; email?: string; fonction?: string } = {};

    if (!nom.trim()) {
      newErrors.nom = 'Le nom est requis';
    }

    if (!prenom.trim()) {
      newErrors.prenom = 'Le prénom est requis';
    }

    if (!email.trim()) {
      newErrors.email = 'L\'email est requis';
    } else if (!/^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(email)) {
      newErrors.email = 'Format d\'email invalide';
    }

    setErrors(newErrors);
    return Object.keys(newErrors).length === 0;
  };

  const getApiRoute = (role: string, id: string): string => {
    const roleLower = role.toLowerCase();

    switch (true) {
      case roleLower.includes('acheteur signataire'):
        return `${process.env.REACT_APP_API_URL}/validateurs-achat/${id}`;

      case roleLower.includes('acheteur'):
        return `${process.env.REACT_APP_API_URL}/acheteurs/${id}`;

      case roleLower.includes('prestataire signataire'):
        return `${process.env.REACT_APP_API_URL}/signataires/${id}`;

      case roleLower.includes('prestataire'):
        return `${process.env.REACT_APP_API_URL}/prestataires/${id}`;

      default:
        return `${process.env.REACT_APP_API_URL}/users/${id}`;
    }
  };

  const handleSubmit = async () => {
    if (!validateForm()) return;

    setIsSubmitting(true);
    try {
      const token = localStorage.getItem('token');

      // Si le rôle a changé, déléguer TOUT le traitement à handleSaveUser (via onSave)
      // pour éviter les doublons d'appels API
      if (role !== userRole) {
        console.log(`Changement de rôle de "${userRole}" vers "${role}" - délégation à handleSaveUser`);

        // Appeler onSave qui gère déjà la création/suppression dans les bonnes tables
        await onSave(userId, { nom, prenom, email, fonction, role });
        onClose();
        return;
      }

      // Pas de changement de rôle - simple mise à jour des informations
      const apiRoute = getApiRoute(userRole, userId);
      console.log(`Mise à jour utilisateur via route: ${apiRoute}`);

      const response = await fetch(apiRoute, {
        method: 'PUT',
        headers: {
          'Authorization': `Bearer ${token}`,
          'Content-Type': 'application/json',
          'Accept': 'application/json'
        },
        body: JSON.stringify({
          nom: nom,
          prenom: prenom,
          email: email.toLowerCase(),
          fonction: fonction
        })
      });

      if (!response.ok) {
        throw new Error(`Erreur API: ${response.status}`);
      }

      // Appeler la fonction de rappel pour mettre à jour l'interface
      await onSave(userId, { nom, prenom, email, fonction, role });
      onClose();
    } catch (error) {
      console.error('Erreur lors de la mise à jour:', error);
      alert(`Erreur: ${error instanceof Error ? error.message : 'Une erreur est survenue'}`);
    } finally {
      setIsSubmitting(false);
    }
  };

  if (!isOpen) return null;

  return (
    <div className="modal-overlay edit-user-modal" onClick={onClose}>
      <div className="modal-container" onClick={(e) => e.stopPropagation()}>
        <div className="modal-header">
          <h2>Modifier les informations utilisateur</h2>
          <button className="modal-close-button" onClick={onClose}>
            <svg xmlns="http://www.w3.org/2000/svg" width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
              <line x1="18" y1="6" x2="6" y2="18"></line>
              <line x1="6" y1="6" x2="18" y2="18"></line>
            </svg>
          </button>
        </div>

        <div className="modal-content-modification">
          <div className="edit-user-form">
            <div className="form-row">
              <div className="form-group">
                <label>Nom</label>
                <input
                  type="text"
                  value={nom}
                  onChange={(e) => setNom(e.target.value)}
                  className={errors.nom ? 'error' : ''}
                  placeholder="Nom"
                />
                {errors.nom && <span className="error-message">{errors.nom}</span>}
              </div>

              <div className="form-group">
                <label>Prénom</label>
                <input
                  type="text"
                  value={prenom}
                  onChange={(e) => setPrenom(e.target.value)}
                  className={errors.prenom ? 'error' : ''}
                  placeholder="Prénom"
                />
                {errors.prenom && <span className="error-message">{errors.prenom}</span>}
              </div>
            </div>

            <div className="form-group">
              <label>Fonction</label>
              <input
                type="text"
                value={fonction}
                onChange={(e) => setFonction(e.target.value)}
                className={errors.fonction ? 'error' : ''}
                placeholder="Fonction"
              />
              {errors.fonction && <span className="error-message">{errors.fonction}</span>}
            </div>

            <div className="form-group">
              <label>Rôle</label>
              <select
                value={role}
                onChange={(e) => setRole(e.target.value)}
                className={errors.role ? 'error' : ''}
              >
                <option value="Acheteur">Acheteur</option>
                <option value="Acheteur signataire">Acheteur signataire</option>
                <option value="Observateur" disabled style={{ color: '#999' }}>
                  Observateur - En cours de développement
                </option>
              </select>
              {errors.role && <span className="error-message">{errors.role}</span>}
            </div>

            <div className="form-group">
              <label>Adresse email</label>
              <input
                type="email"
                value={email}
                onChange={(e) => setEmail(e.target.value)}
                className={errors.email ? 'error' : ''}
                placeholder="email@example.com"
              />
              {errors.email && <span className="error-message">{errors.email}</span>}
            </div>
          </div>

          <div className="modal-actions">
            <Button
              value="Annuler"
              variant="secondary"
              onClick={onClose}
            />
            <Button
              value={isSubmitting ? "Enregistrement..." : "Enregistrer"}
              variant="primary"
              onClick={handleSubmit}
              disabled={isSubmitting}
            />
          </div>
        </div>
      </div>
    </div>
  );
};

export default EditUserModal;